import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Boxes, Loader2, AlertTriangle, Plus, Search, Filter, ShieldAlert, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { listIntentTemplates } from '../services/api';
import { IntentAuthoringConsole } from '../components/v2/intent/IntentAuthoringConsole';
import { CreateIntentTemplateSlideout } from '../components/v2/intent/CreateIntentTemplateSlideout';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Button } from '../components/v2/Button';

/**
 * Intent Profiles Catalog v1.0 — Phase 1E.1
 *
 * Read-only catalog landing page.
 *
 * <p>This is the new dedicated left-nav surface for the Intent Profiles
 * catalog. Lists tenant-visible templates (calls GET /api/v1/intent-templates)
 * with state/scope/risk badges; clicking a row opens a slideout with the
 * version timeline and security-critical flag indicators.</p>
 *
 * <p><b>Phase boundary:</b> Phase 1E.1 ships read-only. The "New template"
 * button is rendered (disabled) so the future Phase 1E.2 entry point is
 * discoverable from day one — operators see "this is where authoring will
 * land" rather than the button appearing out of nowhere later. Hover
 * tooltip explains the deferral.</p>
 *
 * <p><b>No regression:</b> The legacy per-agent inline editor at
 * AgentDetailPageV2 → IntentProfilesEditor stays untouched and remains
 * the runtime source-of-truth until Phase 1D's feature flag flips
 * (and Phase 5 removes the inline column entirely). Operators can use
 * BOTH surfaces concurrently — the catalog acts as the future system of
 * record; the inline editor remains the live-config knob.</p>
 */
export default function IntentProfilesCatalogPageV2() {
  const { hasPermission } = useAuth();
  const canAuthor = hasPermission('compliance.admin') || hasPermission('policy.admin');

  const [state, setState] = useState('loading'); // loading | ready | error | empty
  const [templates, setTemplates] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  // Master-detail: the selected template id (object derived from the list so it
  // stays fresh across reloads); the right pane renders the consolidated
  // IntentAuthoringConsole for it — all authoring (matchers + Rego + dry-run) is inline.
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterState, setFilterState] = useState('ALL'); // ALL | DRAFT | ACTIVE | DEPRECATED | ARCHIVED

  const load = useCallback(async () => {
    setState('loading');
    setErrorMsg(null);
    const res = await listIntentTemplates();
    if (res?.ok === false) {
      setState('error');
      setErrorMsg(res.error || 'Failed to load intent templates');
      return;
    }
    /* apiFetch envelope: { ok, data }. Unwrap defensively. */
    const raw = res?.data ?? res;
    const arr = Array.isArray(raw) ? raw : [];
    setTemplates(arr);
    setState(arr.length === 0 ? 'empty' : 'ready');
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refetch when this page regains focus/visibility. The left list renders a
  // denormalized template.state captured at page-load, while the console reads the
  // live version state — so an approval made on another surface (Approval Center,
  // another tab/session) updates the DB but not this snapshot. Refetch-on-focus keeps
  // the two views in sync instead of trusting a one-time load. (In-page lifecycle
  // actions already refresh via the console's onMutate.)
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  // Auto-select the first template so the authoring console is never an empty gate.
  useEffect(() => {
    if (!selectedTemplateId) {
      const firstLive = templates.find((t) => t.state !== 'ARCHIVED');
      if (firstLive) setSelectedTemplateId(firstLive.id);
    }
  }, [templates, selectedTemplateId]);

  const filtered = useMemo(() => {
    let out = templates;
    if (filterState === 'ALL') {
      // "All" shows live templates; archived are reachable via the Archived chip.
      out = out.filter((t) => t.state !== 'ARCHIVED');
    } else {
      out = out.filter((t) => t.state === filterState);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((t) =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.displayName || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.category || '').toLowerCase().includes(q)
      );
    }
    return out;
  }, [templates, filterState, search]);

  const counts = useMemo(() => {
    const c = { ALL: 0, DRAFT: 0, ACTIVE: 0, DEPRECATED: 0, ARCHIVED: 0 };
    for (const t of templates) {
      if (t.state !== 'ARCHIVED') c.ALL++;
      if (c[t.state] !== undefined) c[t.state]++;
    }
    return c;
  }, [templates]);

  // Derive the selected template from the (freshly-reloaded) list so its etag /
  // activeVersionId stay current after a lifecycle mutation.
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  return (
    <V2PageShell
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <Boxes size={20} />
          Intent Profiles
        </span>
      }
      subtitle="Named, versioned policies that scope which skills an agent may call under a declared business intent. Compliance authors the template — allowed-skill matchers, the OPA/Rego policy, and a dry-run — all in one place here; Developers then bind agents to an approved version (which materializes it to the agent's runtime). Every author / approve / bind action is recorded on the hash-chained audit trail."
      actions={
        <HeaderActions
          canAuthor={canAuthor}
          onReload={load}
          onCreate={() => setCreateOpen(true)}
          loading={state === 'loading'}
        />
      }
    >
      {/* Master-detail: template list (left) + consolidated authoring console (right). */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 340px', minWidth: 300 }}>
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            filterState={filterState}
            onFilterStateChange={setFilterState}
            counts={counts}
          />
          {state === 'loading' && <LoadingState />}
          {state === 'error' && <ErrorState message={errorMsg} onRetry={load} />}
          {state === 'empty' && <EmptyState canAuthor={canAuthor} onCreate={canAuthor ? () => setCreateOpen(true) : null} />}
          {state === 'ready' && (
            <TemplateList
              templates={filtered}
              totalCount={templates.length}
              selectedId={selectedTemplateId}
              onSelect={(t) => setSelectedTemplateId(t.id)}
            />
          )}
        </div>

        <div style={{
          flex: '1 1 auto', minWidth: 0,
          background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)',
          borderRadius: 8, padding: 18, minHeight: 360,
        }}>
          {selectedTemplate ? (
            <IntentAuthoringConsole
              key={selectedTemplate.id}
              template={selectedTemplate}
              onMutate={load}
              onArchived={() => { setSelectedTemplateId(null); load(); }}
            />
          ) : (
            <div style={{
              padding: 48, textAlign: 'center', color: 'var(--v2-text-muted)', fontSize: 13,
            }}>
              <Boxes size={36} color="var(--v2-border-strong)" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--v2-text-primary)', marginBottom: 6 }}>
                Select a template
              </div>
              <p style={{ maxWidth: 460, margin: '0 auto', lineHeight: 1.5 }}>
                Choose a template on the left to author its allowed-skill matchers, OPA/Rego
                policy, and dry-run — then submit it for approval.
              </p>
            </div>
          )}
        </div>
      </div>

      <CreateIntentTemplateSlideout
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={({ templateId }) => {
          // Reload + select the new template; the console opens on its v1 DRAFT
          // so the author lands directly on the consolidated authoring surface.
          load();
          setSelectedTemplateId(templateId);
        }}
      />
    </V2PageShell>
  );
}

/* ── header actions (rendered in V2PageShell's actions slot) ─────── */
function HeaderActions({ canAuthor, onReload, onCreate, loading }) {
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        iconLeft={<RefreshCw className={loading ? 'animate-spin' : ''} />}
        onClick={onReload}
        disabled={loading}
        title="Reload catalog"
      >
        Reload
      </Button>
      <Button
        variant="primary"
        size="sm"
        iconLeft={<Plus />}
        onClick={canAuthor ? onCreate : undefined}
        disabled={!canAuthor}
        title={
          canAuthor
            ? 'Author a new tenant-scoped intent template in DRAFT state.'
            : 'You need compliance.admin or policy.admin to author intent templates.'
        }
      >
        New template
      </Button>
    </>
  );
}

/* ── filter bar ─────────────────────────────────────────────────── */
function FilterBar({ search, onSearchChange, filterState, onFilterStateChange, counts }) {
  const chips = [
    { id: 'ALL',        label: 'All',         color: '#475569', bg: '#f1f5f9' },
    { id: 'DRAFT',      label: 'Draft',       color: '#a16207', bg: '#fef3c7' },
    { id: 'ACTIVE',     label: 'Active',      color: '#15803d', bg: '#dcfce7' },
    { id: 'DEPRECATED', label: 'Deprecated',  color: '#a16207', bg: '#fef3c7' },
    { id: 'ARCHIVED',   label: 'Archived',    color: '#475569', bg: '#e2e8f0' },
  ];
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10,
      marginBottom: 16,
    }}>
      <div style={{ position: 'relative', width: '100%' }}>
        <Search size={14} style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: '#94a3b8', pointerEvents: 'none',
        }} />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by name, description, category…"
          style={{
            width: '100%', padding: '8px 10px 8px 32px', fontSize: 13,
            border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff',
            color: '#0f172a', outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Filter size={14} color="#64748b" />
        {chips.map((c) => {
          const active = filterState === c.id;
          const count = counts[c.id] ?? 0;
          return (
            <button
              key={c.id}
              onClick={() => onFilterStateChange(c.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', fontSize: 12, fontWeight: 600,
                color: active ? '#fff' : c.color,
                background: active ? '#0f172a' : c.bg,
                border: '1px solid ' + (active ? '#0f172a' : 'transparent'),
                borderRadius: 999, cursor: 'pointer',
              }}
            >
              {c.label}
              <span style={{
                fontSize: 11, fontWeight: 500,
                color: active ? '#cbd5e1' : '#64748b',
              }}>{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── template list ──────────────────────────────────────────────── */
function TemplateList({ templates, totalCount, selectedId, onSelect }) {
  if (templates.length === 0) {
    return (
      <div style={{
        padding: 32, textAlign: 'center', color: '#64748b', fontSize: 13,
        background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8,
      }}>
        No templates match the current filter ({totalCount} total).
      </div>
    );
  }
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
      overflow: 'hidden',
    }}>
      {templates.map((t, idx) => (
        <TemplateRow
          key={t.id}
          template={t}
          isLast={idx === templates.length - 1}
          selected={t.id === selectedId}
          onClick={() => onSelect(t)}
        />
      ))}
    </div>
  );
}

function TemplateRow({ template, isLast, selected, onClick }) {
  const baseBg = selected ? '#eff6ff' : '#fff';
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'block', textAlign: 'left',
        padding: '14px 18px', background: baseBg, border: 'none',
        borderLeft: selected ? '3px solid #2563eb' : '3px solid transparent',
        borderBottom: isLast ? 'none' : '1px solid #e2e8f0',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = selected ? '#eff6ff' : '#f8fafc'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = baseBg; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
              {template.displayName || template.name}
            </div>
            <StateBadge state={template.state} />
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, SFMono-Regular, monospace', marginBottom: 6 }}>
            {template.name}
          </div>
          {template.description && (
            <div style={{
              fontSize: 12, color: '#475569', lineHeight: 1.4, marginTop: 4,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {template.description}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <ScopeBadge scope={template.scopeLevel} />
            <RiskBadge tier={template.riskTier} />
          </div>
          {template.category && (
            <div style={{ fontSize: 11, color: '#64748b' }}>{template.category}</div>
          )}
        </div>
      </div>
    </button>
  );
}

/* ── empty / loading / error ────────────────────────────────────── */
function LoadingState() {
  return (
    <div style={{
      padding: 48, textAlign: 'center', color: '#64748b', fontSize: 13,
    }}>
      <Loader2 size={20} className="animate-spin" style={{ marginBottom: 8 }} />
      <div>Loading intent templates…</div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div style={{
      background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
      padding: 20, display: 'flex', gap: 12,
    }}>
      <AlertTriangle size={18} color="#991b1b" style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>
          Failed to load templates
        </div>
        <div style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 10 }}>{message}</div>
        <button
          onClick={onRetry}
          style={{
            padding: '6px 12px', fontSize: 12, fontWeight: 600,
            color: '#fff', background: '#dc2626', border: 'none', borderRadius: 6,
            cursor: 'pointer',
          }}
        >Retry</button>
      </div>
    </div>
  );
}

function EmptyState({ canAuthor, onCreate }) {
  return (
    <div style={{
      padding: 48, textAlign: 'center', background: '#f8fafc',
      border: '1px dashed #cbd5e1', borderRadius: 8,
    }}>
      <Boxes size={40} color="#cbd5e1" style={{ marginBottom: 12 }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>
        No intent templates yet
      </div>
      <p style={{ fontSize: 13, color: '#64748b', maxWidth: 520, margin: '0 auto', lineHeight: 1.5 }}>
        Intent templates let Compliance author named, versioned policies that scope what skills
        an agent may call under a declared business intent.
        {!canAuthor && ' This catalog will populate once a Compliance role authors the first template.'}
      </p>
      {canAuthor && onCreate && (
        <div style={{ marginTop: 16 }}>
          <Button variant="primary" size="sm" iconLeft={<Plus />} onClick={onCreate}>
            Author first template
          </Button>
        </div>
      )}
    </div>
  );
}

/* ── badges (shared with slideout but inlined to avoid coupling) ──── */
function StateBadge({ state }) {
  const styles = {
    DRAFT:      { color: '#a16207', bg: '#fef3c7' },
    ACTIVE:     { color: '#15803d', bg: '#dcfce7' },
    DEPRECATED: { color: '#a16207', bg: '#fef3c7' },
    ARCHIVED:   { color: '#475569', bg: '#e2e8f0' },
  };
  const s = styles[state] || styles.DRAFT;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color: s.color, background: s.bg,
      padding: '2px 6px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>{state || 'UNKNOWN'}</span>
  );
}

function ScopeBadge({ scope }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color: '#0369a1', background: '#e0f2fe',
      padding: '2px 6px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>{scope || 'UNKNOWN'}</span>
  );
}

function RiskBadge({ tier }) {
  const styles = {
    LOW:      { color: '#15803d', bg: '#dcfce7' },
    MEDIUM:   { color: '#0369a1', bg: '#e0f2fe' },
    HIGH:     { color: '#a16207', bg: '#fef3c7' },
    CRITICAL: { color: '#991b1b', bg: '#fee2e2' },
  };
  const s = styles[tier] || styles.MEDIUM;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 10, fontWeight: 600, color: s.color, background: s.bg,
      padding: '2px 6px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {tier === 'CRITICAL' && <ShieldAlert size={9} />}
      {tier || 'UNKNOWN'}
    </span>
  );
}
